import {AgentSessionsTokenProvider} from '@github-ui/copilot-auth-token/agent-sessions-token'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {makeCAPIRequest, type CAPIRequestOptions} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {getEnv} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopilotAuthTokenProvider} from '@github-ui/copilot-auth-token'

const DEFAULT_API_URL = 'https://api.githubcopilot.com'

interface FetchWithAuthOptions {
  path: string
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  streamingResponse?: boolean
  basePath?: string
  body?: object
}

export class FetchCAPIError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'FetchCAPIError'
    this.status = status
  }
}

export async function fetchCAPI({
  path,
  method = 'GET',
  streamingResponse = false,
  body,
}: FetchWithAuthOptions): Promise<Response> {
  const useCAPITokenProvider = isFeatureEnabled('mission_control_use_capi_token_provider')
  const tokenProvider = useCAPITokenProvider ? new CopilotAuthTokenProvider([]) : new AgentSessionsTokenProvider()
  const token = await tokenProvider.getAuthToken()

  if (!token) {
    throw new Error('No token available')
  }

  const requestOptions: CAPIRequestOptions = {
    authToken: token,
    basePath: getEnv().copilotApiOverrideUrl ?? DEFAULT_API_URL,
    method,
    path,
    streamingResponse,
    body,
  }
  const response = await makeCAPIRequest(requestOptions)

  if (!response.ok) {
    // response is a FailedAPIResult in this block
    let errorMessage = 'error' in response ? response.error : `Failed CAPI request (${response.status})`

    // Try to get the actual error message from the response body (it's plain text)
    if ('response' in response && response.response) {
      try {
        const text = await response.response.text()
        if (text && text.trim()) {
          errorMessage = text.trim()
        }
      } catch {
        // If we can't read the response body, keep the generic message
      }
    }

    throw new FetchCAPIError(errorMessage, response.status)
  }

  return response
}
